// Printable timetables (A4 landscape): a month of prayer times, or Ramadan.
// Built from the same data as the Prayer Times page, so they always match.
import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const PRAYERS = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];

const s = StyleSheet.create({
    page: { padding: 24, fontFamily: 'Helvetica', fontSize: 8, color: '#1f2937' },
    head: { marginBottom: 10, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' },
    name: { fontSize: 16, fontFamily: 'Helvetica-Bold' },
    sub: { fontSize: 9, color: '#6b7280', marginTop: 2 },
    title: { fontSize: 12, fontFamily: 'Helvetica-Bold', textAlign: 'right' },
    row: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb' },
    shade: { backgroundColor: '#f3f4f6' },
    friday: { backgroundColor: '#ecfdf5' },
    headRow: { flexDirection: 'row', backgroundColor: '#0f5132', color: '#ffffff', fontFamily: 'Helvetica-Bold' },
    cell: { paddingVertical: 3, paddingHorizontal: 2, textAlign: 'center' },
    foot: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', fontSize: 8, color: '#4b5563' },
});

// "05:00 (GMT)" -> "05:00"
export const hhmm = (v) => {
    const m = String(v || '').match(/(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
};

// "01-11-2026" -> Date
export const parseDate = (ddmmyyyy) => {
    const [d, m, y] = String(ddmmyyyy || '').split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

const Table = ({ columns, rows }) => (
    <View>
        <View style={s.headRow} fixed>
            {columns.map((c) => <Text key={c.label} style={[s.cell, { width: c.width }]}>{c.label}</Text>)}
        </View>
        {rows.map((r, i) => (
            <View key={i} style={[s.row, i % 2 ? s.shade : null, r.friday ? s.friday : null]} wrap={false}>
                {columns.map((c) => <Text key={c.label} style={[s.cell, { width: c.width }]}>{c.value(r)}</Text>)}
            </View>
        ))}
    </View>
);

const Shell = ({ masjid, title, subtitle, jummah, note, time = hhmm, children }) => (
    <Document title={`${masjid?.name || 'Masjid'} ${title}`}>
        <Page size="A4" orientation="landscape" style={s.page}>
            <View style={s.head}>
                <View>
                    <Text style={s.name}>{masjid?.name || 'Masjid'}</Text>
                    {masjid?.location?.line1 ? <Text style={s.sub}>{masjid.location.line1}</Text> : null}
                </View>
                <View>
                    <Text style={s.title}>{title}</Text>
                    {subtitle ? <Text style={[s.sub, { textAlign: 'right' }]}>{subtitle}</Text> : null}
                </View>
            </View>
            {children}
            <View style={s.foot}>
                <Text>
                    {jummah?.length ? `Jumu'ah: ${jummah.map((j, i) => `${jummah.length > 1 ? `${i + 1}) ` : ''}khutbah ${time(j.athanTime)}, iqamah ${time(j.iqamahTime)}`).join('   ')}` : ''}
                </Text>
                <Text>{note || 'Times show Athan / Iqamah.'}</Text>
            </View>
        </Page>
    </Document>
);

const weekday = (r) => parseDate(r.date?.gregorian?.date).toLocaleDateString('en-GB', { weekday: 'short' });
const isFriday = (r) => parseDate(r.date?.gregorian?.date).getDay() === 5;

// `time` shows a time in the masjid's format (General Settings).
export const MonthTimetable = ({ masjid, monthLabel, days, jummah, time = hhmm }) => {
    // Date, Day, then each prayer's Athan and Iqamah, with Sunrise after Fajr.
    const prayer = (p) => [
        { label: p, width: '8%', value: (r) => time(r.timings?.[p]?.startTime) },
        { label: 'Iqamah', width: '8%', value: (r) => time(r.timings?.[p]?.iqamahTime) },
    ];
    const columns = [
        { label: 'Date', width: '6%', value: (r) => parseDate(r.date?.gregorian?.date).getDate() },
        { label: 'Day', width: '6%', value: weekday },
        ...prayer('Fajr'),
        { label: 'Sunrise', width: '8%', value: (r) => time(r.timings?.Sunrise) },
        ...PRAYERS.slice(1).flatMap(prayer),
    ];
    return (
        <Shell masjid={masjid} title="Prayer timetable" subtitle={monthLabel} jummah={jummah} time={time}>
            <Table columns={columns} rows={days.map((d) => ({ ...d, friday: isFriday(d) }))} />
        </Shell>
    );
};

export const RamadanTimetable = ({ masjid, yearLabel, days, jummah, time = hhmm }) => {
    const columns = [
        { label: 'Ramadan', width: '8%', value: (r) => r.ramadanDay },
        { label: 'Date', width: '11%', value: (r) => parseDate(r.date?.gregorian?.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) },
        { label: 'Day', width: '7%', value: weekday },
        { label: 'Sehri ends', width: '10%', value: (r) => time(r.specialSalah?.imsak) || time(r.timings?.Fajr?.startTime) },
        { label: 'Fajr', width: '9%', value: (r) => time(r.timings?.Fajr?.iqamahTime) || time(r.timings?.Fajr?.startTime) },
        { label: 'Sunrise', width: '9%', value: (r) => time(r.timings?.Sunrise) },
        { label: 'Dhuhr', width: '9%', value: (r) => time(r.timings?.Dhuhr?.iqamahTime) },
        { label: 'Asr', width: '9%', value: (r) => time(r.timings?.Asr?.iqamahTime) },
        { label: 'Iftar', width: '9%', value: (r) => time(r.timings?.Maghrib?.startTime) },
        { label: 'Isha', width: '9%', value: (r) => time(r.timings?.Isha?.iqamahTime) },
        { label: 'Taraweeh', width: '10%', value: (r) => time(r.specialSalah?.taraweeh) || '' },
    ];
    return (
        <Shell masjid={masjid} title="Ramadan timetable" subtitle={yearLabel} jummah={jummah}
            note="Prayer columns show Iqamah. Iftar is at Maghrib.">
            <Table columns={columns} rows={days.map((d) => ({ ...d, friday: isFriday(d) }))} />
        </Shell>
    );
};
